import React from 'react';
import { Ref } from 'react-hook-form';
import {
  IconButton,
  makeStyles,
  TextField,
  Theme,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { CancelRounded } from '@material-ui/icons';
import { colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import {
  NumberFormatCustom,
  NumberFormatCustomWithDecimalPoint
} from '@components/CurrencyTextfield/CurrncyTextField';
import { UploadedFileWithProperty } from './RequestFormFileInput';
import useQueryString from '@hooks/useQueryString';
import { RequestQueryString } from '../types';
import { CapaPcbServiceType } from '@generated/graphql';

export interface UploadedFileListEntryProps extends UploadedFileWithProperty {
  type: 'drawing' | 'BOM' | 'gerber';
  onClickDeleteFile: (fileId: string) => void;
  onUpdateFile: (
    fileId: string,
    updateFileData: Partial<UploadedFileWithProperty>
  ) => void;
}

interface UploadedFileListEntryStyleProps {
  type: 'drawing' | 'BOM' | 'gerber';
  assemblyOnly: boolean;
}

const UploadedFileListEntry = React.forwardRef<Ref, UploadedFileListEntryProps>(
  (file, ref) => {
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down('sm'));
    const {
      fileId,
      fileName,
      quantity,
      thickness,
      type,
      onClickDeleteFile,
      onUpdateFile
    } = file;
    const {
      query: { process }
    } = useQueryString<RequestQueryString>();
    const assemblyOnly =
      (process ?? '').includes(CapaPcbServiceType.CircuitAssembly) &&
      !(process ?? '').includes(CapaPcbServiceType.CircuitFabrication);
    const { input, uploadedFileEntry } = useUploadedFileListEntryStyles({
      type,
      assemblyOnly
    });

    return (
      <div className={uploadedFileEntry}>
        <div className="fileName">
          <CustomizedTypography variant="body2" color="textPrimary" noWrap>
            {fileName.replace(/_ateamgerber/, '')}
          </CustomizedTypography>
          {matches && (
            <IconButton
              onClick={() => onClickDeleteFile(fileId)}
              data-cy={`rfq-drawingFile-close-${fileId}`}
            >
              <CancelRounded />
            </IconButton>
          )}
        </div>
        <div className="fileProperties">
          {type === 'gerber' && !assemblyOnly && (
            <TextField
              autoComplete="off"
              className={input}
              variant="outlined"
              placeholder={!matches ? '0' : '두께입력'}
              inputRef={ref}
              InputProps={{
                inputComponent: NumberFormatCustomWithDecimalPoint,
                onChange: (e) => {
                  const value = (e.target as HTMLInputElement).value;
                  onUpdateFile(fileId, {
                    thickness: value
                  });
                },
                endAdornment: (
                  <CustomizedTypography variant="body2" color="textSecondary">
                    mm
                  </CustomizedTypography>
                )
              }}
              defaultValue={file.thickness ?? null}
              value={thickness ?? ''}
            />
          )}

          {(type === 'BOM' || (type === 'gerber' && !assemblyOnly)) && (
            <TextField
              autoComplete="off"
              className={input}
              variant="outlined"
              placeholder={!matches ? '0' : '개수입력'}
              inputRef={ref}
              InputProps={{
                inputComponent: NumberFormatCustom,
                onChange: (e) => {
                  const value = (e.target as HTMLInputElement).value;
                  onUpdateFile(fileId, {
                    quantity: value
                  });
                },
                endAdornment: (
                  <CustomizedTypography variant="body2" color="textSecondary">
                    PCS
                  </CustomizedTypography>
                )
              }}
              defaultValue={file.quantity ?? null}
              value={quantity ?? ''}
            />
          )}
          {!matches && (
            <IconButton
              onClick={() => onClickDeleteFile(fileId)}
              data-cy={`rfq-drawingFile-close-${fileId}`}
            >
              <CancelRounded />
            </IconButton>
          )}
        </div>
      </div>
    );
  }
);

export default UploadedFileListEntry;

const useUploadedFileListEntryStyles = makeStyles<
  Theme,
  UploadedFileListEntryStyleProps
>((theme) => ({
  uploadedFileEntry: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    height: 68,
    borderRadius: 8,
    backgroundColor: colorSet.gray200,
    padding: '0px 12px',
    paddingRight: '8px',
    maxWidth: 'unset',
    [theme.breakpoints.down('sm')]: {
      maxWidth: () => 'unset',
      flexDirection: 'column',
      gap: '8px',
      justifyContent: 'center',
      height: ({ assemblyOnly, type }) =>
        assemblyOnly && type === 'gerber' ? 48 : 106
    },
    '& .fileName': {
      width: 'calc(100% - 270px)',
      position: 'relative',
      display: 'flex',
      gap: '9px',
      [theme.breakpoints.down('sm')]: {
        width: ' 100%',
        height: '20px',
        justifyContent: 'space-between',
        alignItems: 'center'
      }
    },
    '& .fileProperties': {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      [theme.breakpoints.down('sm')]: {
        width: ' 100%',
        gap: '8px'
      }
    },
    '& svg': {
      width: 20,
      height: 20
    },
    '& button': {
      width: 32,
      height: 32,
      marginLeft: '2px'
    }
  },
  input: {
    maxWidth: 110,
    borderRadius: '8px',
    backgroundColor: colorSet.gray000,
    [theme.breakpoints.down('sm')]: {
      flex: 1,
      maxWidth: 'unset'
    },
    '& input': {
      borderRadius: '8px',
      padding: '10px 4px 10px 12px',
      textAlign: 'right',
      '&::placeholder': {
        color: colorSet.gray500
      }
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: colorSet.gray400
    }
  }
}));
